import styled, { keyframes } from 'styled-components';
import type { KadryOverviewFunnel } from '@entities/kadry';
import { Hint, Panel, PanelHead, pct } from './ui';

/* Воронка подбора: от найденных кандидатов до подтверждённой связи.
   Ширина полосы — доля от найденных; справа — переход с прошлого шага. */

const grow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Step = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  align-items: baseline;
`;

const Label = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & > strong {
    margin-right: ${({ theme }) => theme.spacing(2)};
    font-size: 16px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Conv = styled.div`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Track = styled.div`
  grid-column: 1 / -1;
  height: 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

const Fill = styled.div<{ $share: number; $delay: number; $last: boolean }>`
  height: 100%;
  width: ${({ $share }) => `${Math.max($share * 100, 1.5)}%`};
  border-radius: inherit;
  background: ${({ theme, $last }) => ($last ? theme.colors.status.success : theme.colors.primary)};
  opacity: ${({ $share }) => ($share > 0 ? 1 : 0.25)};
  transform-origin: left center;
  animation: ${grow} 700ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
`;

export function FunnelPanel({ f, delay = 0 }: { f: KadryOverviewFunnel; delay?: number }) {
  const analyzed = f.analyzed_ai + f.analyzed_other;
  const steps = [
    { label: 'найдено на площадках', n: f.found, from: null as number | null },
    { label: 'резюме разобрано целиком', n: analyzed, from: f.found },
    { label: 'отправлено предложений', n: f.offered, from: analyzed },
    { label: 'ответили «Подходит»', n: f.interested, from: f.offered },
    { label: 'связь подтверждена', n: f.contacted, from: f.interested },
  ];
  return (
    <Panel $delay={delay}>
      <PanelHead>
        <h3>Воронка подбора</h3>
        <span>от найденных до звонка</span>
      </PanelHead>
      <Steps>
        {steps.map((s, i) => (
          <Step key={s.label}>
            <Label>
              <strong>{s.n}</strong>
              {s.label}
            </Label>
            <Conv>{s.from == null ? '' : `${pct(s.n, s.from)} с прошлого шага`}</Conv>
            <Track>
              <Fill
                $share={f.found ? s.n / f.found : 0}
                $delay={delay + i * 90}
                $last={i === steps.length - 1}
              />
            </Track>
          </Step>
        ))}
      </Steps>
      <Hint>
        Целиком открываются лучшие резюме прогона — остальные оценены по краткой выдаче площадки (
        {f.preliminary}). Разбор резюме: DeepSeek — {f.analyzed_ai}, правилами — {f.analyzed_other}.
      </Hint>
    </Panel>
  );
}
